import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material'
import { useRef } from 'react'
import TextFreeQuestion from '../questionTypes/TextFreeQuestion'
import TransmissionDatePicker from '../questionTypes/TransmissionDatePicker'
import TransmissionPJ from '../questionTypes/TransmissionPJ/TransmissionPJ'
import TransmissionSketcher from '../questionTypes/TransmissionSketcher'
import TransmissionSlider from '../questionTypes/TransmissionSlider'
import { PlannedExpertiseWorkflowRecapProps } from './_props'
import { recapViewSx } from './_styles'
import { Banner } from '@workspace/frontend-kit'
import CommonTooltip from 'libs/frontend-kit/src/lib/components/common/tooltip/Tooltip'
import LockIcon from '@mui/icons-material/Lock'

const getValue = (answersByQuestionId: Map<string, any[]>, question: any) => {
    const answer = answersByQuestionId.get(String(question.id)) ?? []
    return question.inputType === 'pj' || question.multiple ? answer : answer[0]
}

export default function PlannedExpertiseWorkflowRecap({
    questions,
    answersByQuestionId,
    onAnswer,
}: PlannedExpertiseWorkflowRecapProps) {
    // An optional text field stays editable once it has been editable, even if the user clears it
    const editableTextQuestionIds = useRef<Set<string>>(new Set())

    const renderReadOnlyValue = (question: any, value: any) => {
        if (question.inputType === 'choice') {
            const selectedIds = Array.isArray(value) ? value.map(String) : [String(value ?? '')]
            return (
                question.choices
                    ?.filter((choice: any) => selectedIds.includes(String(choice.id)))
                    .map((choice: any) => choice.label)
                    .join(', ') || 'Réponse non renseignée'
            )
        }

        if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
            return 'Réponse non renseignée'
        }

        if (Array.isArray(value)) {
            return value.map((item) => item?.name ?? item).join(', ')
        }

        return String(value)
    }

    const renderAnswer = (question: any, value: any) => {
        const questionId = String(question.id)
        const update = (nextValue: any) => onAnswer(questionId, nextValue)
        const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0)

        if (question.inputType === 'text' && !(question.optional && isEmpty)) {
            editableTextQuestionIds.current.add(questionId)
        }

        if (question.inputType === 'text' && editableTextQuestionIds.current.has(questionId)) {
            return (
                <TextFreeQuestion question={question} initialValue={value ?? ''} onAnswer={update} showInitialError />
            )
        }

        if (question.inputType === 'pj') {
            return <TransmissionPJ question={question} initialValue={value ?? null} onAnswer={update} />
        }

        if (question.inputType === 'slider') {
            return (
                <TransmissionSlider
                    question={{
                        ...question,
                        minimumValue: question.sliderMin,
                        maximumValue: question.sliderMax,
                        step: question.sliderStep,
                    }}
                    initialValue={value ?? question.sliderMin ?? 0}
                    onAnswer={update}
                />
            )
        }

        if (question.inputType === 'date') {
            return <TransmissionDatePicker
                    question={question}
                    initialValue={value ?? ''}
                    onAnswer={update}
                    showInitialError
                />
        }

        if (question.inputType === 'sketch') {
            return <TransmissionSketcher question={question} initialValue={value ?? ''} readOnly />
        }

        const TooltipTitle = () => (
            <Box display="flex" alignItems="center" width="fit-content">
                <LockIcon sx={{ marginRight: '0.5rem' }} />
                <Typography>Saisie non modifiable</Typography>
            </Box>
        )

        return (
            <CommonTooltip
                title={<TooltipTitle />}
                componentsProps={{
                    tooltip: {
                        sx: {
                            minWidth: 0,
                        },
                    },
                }}
            >
                <Box
                    aria-disabled="true"
                    sx={[
                        recapViewSx.tooltipContentContainer,
                        isEmpty
                            ? recapViewSx.tooltipContentDisabledEmptyContainer
                            : recapViewSx.tooltipContentDisabledFilledContainer,
                    ]}
                >
                    <Typography
                        sx={[
                            recapViewSx.tooltipContentText,
                            isEmpty
                                ? recapViewSx.tooltipContentDisabledEmptyText
                                : recapViewSx.tooltipContentDisabledFilledText,
                        ]}
                    >
                        {renderReadOnlyValue(question, value)}
                    </Typography>
                </Box>
            </CommonTooltip>
        )
    }

    return (
        <Stack sx={recapViewSx.recapWrapper}>
            <Stack gap="2rem">
                <Stack>
                    <Typography sx={recapViewSx.recapTitle}>Récapitulatif de la demande</Typography>
                    <Typography variant="body1" sx={recapViewSx.recapSubtitle}>
                        Avant de transmettre, vérifiez que tout est en ordre
                    </Typography>
                </Stack>
                <Banner
                    title="Limitation de la modification des réponses"
                    text="Les modifications sont possibles, pour les champs de texte libre, les pièces jointes, ainsi que les échelles de valeur et les dates"
                    severity="NEUTRAL"
                    slotOverrides={{
                        container: {
                            boxSizing: 'border-box',
                        },
                    }}
                />
            </Stack>
            {questions.map((question) => {
                const questionId = String(question.id)
                const value = getValue(answersByQuestionId, question)

                return (
                    <Stack key={questionId} sx={recapViewSx.recapItem}>
                        <Typography sx={recapViewSx.recapQuestion}>{question.title}</Typography>
                        <Box sx={recapViewSx.recapAnswer}>{renderAnswer(question, value)}</Box>
                    </Stack>
                )
            })}
        </Stack>
    )
}
